import ROUTES from '~/shared/constants/routes.const'
import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import {
  FLORIDA_COMING_SOON_MIN_GRADE,
  FLORIDA_VALID_MIN_GRADE,
  NORWAY_COMING_SOON_MIN_GRADE,
  NORWAY_VALID_MIN_GRADE,
  USA_COMING_SOON_MIN_GRADE,
  TestGeneratorPrefillParam,
  USA_VALID_MIN_GRADE,
} from '~/tests/constants/test-generator.const'

interface TestGeneratorPrefill {
  /** The curriculum the link was built from — the generator ignores the prefill if it differs. */
  countryCode: string
  gradeLevel: number
  /** A curriculum topic's `subjectOrder` — the only id both sides share. */
  topicOrder?: number
  /** Where the generator's back button should return to. Omitted means `/test/start`. */
  returnTo?: string
}

/**
 * A path on this site, and nothing else.
 *
 * The return path rides in a query string, so it is whatever the last person to edit the address
 * bar decided it was — and a back button that follows `//evil.example` or `https://evil.example`
 * is an open redirect wearing a back arrow. Only a single leading slash passes: browsers normalise
 * `/\evil.example` to `//evil.example`, so a backslash in that position is refused with it, as is
 * any whitespace, which is how a payload gets smuggled past a naiver check.
 */
const INTERNAL_PATH = /^\/(?![/\\])[^\s\\]*$/

/**
 * Where the generator's back button goes: the page the user actually came from when the link said
 * so and the value is safe to follow, and `/test/start` in every other case — which is where it
 * always went before, and is still right for the generator's own entry point, whose link carries
 * no return path at all.
 */
export const resolveTestGeneratorReturnPath = (value: string | string[] | undefined): string => {
  const candidate = Array.isArray(value) ? value[0] : value
  return candidate && INTERNAL_PATH.test(candidate) ? candidate : ROUTES.TEST_START
}

/**
 * Deep link into the test generator with the grade dropdown selected and, when a topic is given,
 * that topic already checked. Topics are joined on `subjectOrder`: the generator's own topic key
 * mixes in a title that differs between curriculum v1 and v2.
 */
export const buildTestGeneratorUrl = ({
  countryCode,
  gradeLevel,
  topicOrder,
  returnTo,
}: TestGeneratorPrefill): string => {
  const params = new URLSearchParams({
    [TestGeneratorPrefillParam.COUNTRY]: countryCode,
    [TestGeneratorPrefillParam.GRADE]: `${gradeLevel}`,
  })

  if (topicOrder !== undefined) {
    params.set(TestGeneratorPrefillParam.TOPIC, `${topicOrder}`)
  }

  // Checked on the way out as well as on the way in, so a bad path is never handed to a user in a
  // link we wrote ourselves — the reader's guard is for the address bar, not for us.
  if (returnTo && INTERNAL_PATH.test(returnTo)) {
    params.set(TestGeneratorPrefillParam.RETURN_TO, returnTo)
  }

  return `${ROUTES.TEST_GENERATE}?${params.toString()}`
}

export const scrollToTopSmooth = (): void => {
  if (typeof window !== 'undefined') {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }
}

const COUNTRY_GRADE_CONSTANTS: Record<string, { comingSoonMin: number; validMin: number }> = {
  [CurriculumCountryCodes.USA]: {
    comingSoonMin: USA_COMING_SOON_MIN_GRADE,
    validMin: USA_VALID_MIN_GRADE,
  },
  [CurriculumCountryCodes.NORWAY]: {
    comingSoonMin: NORWAY_COMING_SOON_MIN_GRADE,
    validMin: NORWAY_VALID_MIN_GRADE,
  },
  [CurriculumCountryCodes.FLORIDA]: {
    comingSoonMin: FLORIDA_COMING_SOON_MIN_GRADE,
    validMin: FLORIDA_VALID_MIN_GRADE,
  },
}

export const getTestGenerationStatus = (
  gradeLevel: number,
  curriculumCountryCode: CurriculumCountryCodes,
): { isAllowed: boolean; isComingSoon: boolean; isNotAllowed: boolean } => {
  const constants = COUNTRY_GRADE_CONSTANTS[curriculumCountryCode]

  if (!constants) {
    const isComingSoon = gradeLevel >= 5
    const isAllowed = false
    return {
      isAllowed,
      isComingSoon,
      isNotAllowed: !isAllowed && !isComingSoon,
    }
  }

  const isAllowed = gradeLevel >= constants.validMin
  const isComingSoon = gradeLevel < constants.validMin && gradeLevel >= constants.comingSoonMin
  return {
    isAllowed,
    isComingSoon,
    isNotAllowed: !isAllowed && !isComingSoon,
  }
}
